import React from 'react';
import { StyleSheet, View } from 'react-native';
import { colors, hairline, radii, spacing } from '../../theme';
import Text from '../ui/Text';

/**
 * A stand-in for a payment brand's logo - "GP", "Pe", "Visa".
 *
 * ---------------------------------------------------------------------------
 * WHY NOT THE REAL LOGOS
 * ---------------------------------------------------------------------------
 * Two reasons, and the second is the one that matters.
 *
 * G Pay, PhonePe, Visa and the rest are trademarks with usage rules - colour,
 * clear space, minimum size - and none of those assets are in `assets/`. A
 * hand-drawn approximation of someone else's mark is worse than no mark.
 *
 * More importantly, `src/theme/colors.ts` says the six greens are the only
 * greens in the app and that nothing else may be sampled in. Brand marks drag
 * in six unrelated hues, and a payment sheet is exactly where a palette goes
 * to die. Neutral lettermarks keep the page in the app's own ink and make it
 * obvious at a glance that these are placeholders awaiting real artwork.
 *
 * Drop the licensed assets into `assets/pay/` and swap this component's body
 * for an `Image`; nothing that renders it needs to change.
 */

type Props = {
  /** Initials, from `upiApps[].mark` or a card network's name. */
  label: string;
  /** `sm` for a row's leading slot, `md` for the app grid. */
  size?: 'sm' | 'md';
};

const BOX = { sm: 26, md: 38 } as const;

function BrandMark({ label, size = 'md' }: Props) {
  const side = BOX[size];

  return (
    <View
      style={[styles.mark, { width: side, height: side }]}
      // The name is already beside it in every usage, so the tile itself is
      // decoration and repeating it would read the label twice.
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
    >
      <Text
        variant={size === 'sm' ? 'eyebrow' : 'labelSm'}
        color={colors.inkMuted}
        numberOfLines={1}
      >
        {label}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  mark: {
    borderRadius: radii.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surface,
    paddingHorizontal: spacing.xs,
    ...hairline,
  },
});

export default BrandMark;
